import { useCallback, useEffect, useRef, useState } from 'react'
import actionCable from 'app/frontend/base/services/actionCable'

type PresenceViewer = {
  key: string
  user_id: number | null
  label: string | null
}

type PresencePayload = {
  type?: string
  viewers?: PresenceViewer[]
}

type PresenceSubscription = {
  perform: (action: string, payload: unknown) => void
  unsubscribe: () => void
}

type Options<TPayload extends PresencePayload> = {
  channelName: string
  channelId?: number
  channelIdParam: string
  onPayload?: (payload: TPayload) => void
  heartbeatIntervalMs?: number
}

const usePresenceChannel = <TPayload extends PresencePayload>({
  channelName,
  channelId,
  channelIdParam,
  onPayload,
  heartbeatIntervalMs = 15000,
}: Options<TPayload>) => {
  const [viewers, setViewers] = useState<PresenceViewer[]>([])
  const [requiresReload, setRequiresReload] = useState(false)
  const subscriptionRef = useRef<PresenceSubscription>()
  const onPayloadRef = useRef(onPayload)
  const disconnectedAtRef = useRef<number | null>(null)
  const reloadTimeoutRef = useRef<number>()

  useEffect(() => {
    onPayloadRef.current = onPayload
  }, [onPayload])

  useEffect(() => {
    if (!channelId) return

    const clearReloadTimeout = () => {
      if (reloadTimeoutRef.current === undefined) return
      window.clearTimeout(reloadTimeoutRef.current)
      reloadTimeoutRef.current = undefined
    }

    const markDisconnected = () => {
      if (disconnectedAtRef.current !== null) return
      disconnectedAtRef.current = Date.now()
      clearReloadTimeout()
      reloadTimeoutRef.current = window.setTimeout(() => {
        setRequiresReload(true)
      }, 15000)
    }

    const markConnected = () => {
      disconnectedAtRef.current = null
      clearReloadTimeout()
      setRequiresReload(false)
    }

    const subscription = actionCable.subscriptions.create(
      {
        channel: channelName,
        [channelIdParam]: channelId,
      },
      {
        connected: markConnected,
        disconnected: markDisconnected,
        rejected: markDisconnected,
        received: (payload: TPayload) => {
          if (payload.type === 'presence') {
            setViewers(payload.viewers ?? [])
          }

          onPayloadRef.current?.(payload)
        },
      },
    )

    subscriptionRef.current = subscription
    const heartbeat = window.setInterval(() => {
      subscription.perform('heartbeat', {})
    }, heartbeatIntervalMs)

    return () => {
      clearReloadTimeout()
      disconnectedAtRef.current = null
      setRequiresReload(false)
      window.clearInterval(heartbeat)
      subscription.unsubscribe()
      subscriptionRef.current = undefined
      setViewers([])
    }
  }, [channelName, channelId, channelIdParam, heartbeatIntervalMs])

  const perform = useCallback((action: string, payload: unknown) => {
    subscriptionRef.current?.perform(action, payload)
  }, [])

  return {
    viewers,
    requiresReload,
    perform,
  }
}

export type { PresenceViewer, PresencePayload }
export default usePresenceChannel
